import React, { useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, resolveAvatar } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Shield, User, Building2, Camera, Loader2 } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser({ ...user, avatar: data.avatar });
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-page">
      <div>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground ashoka-underline inline-block">My Profile</h2>
        <p className="text-muted-foreground mt-3">Your account information</p>
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="bg-primary text-primary-foreground p-8 flex items-center gap-5">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-primary-foreground/15 border-2 border-primary-foreground/30 flex items-center justify-center text-3xl font-bold overflow-hidden">
              {user?.avatar ? <img src={resolveAvatar(user.avatar)} alt={user.name} className="w-full h-full object-cover" /> : user?.name?.[0]?.toUpperCase()}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="profile-photo-input" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="profile-photo-upload-button"
              title="Change photo"
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center border-2 border-primary hover:opacity-90 transition-opacity"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            </button>
          </div>
          <div>
            <h3 className="font-heading text-2xl font-bold">{user?.name}</h3>
            <p className="text-primary-foreground/80 capitalize">{user?.role}</p>
          </div>
        </div>
        <div className="p-8 space-y-4">
          <Row icon={User} label="Full Name" value={user?.name} />
          <Row icon={Mail} label="Email" value={user?.email} />
          <Row icon={Shield} label="Role" value={user?.role} capitalize />
          <Row icon={Building2} label="Organization" value="Government of Bihar" />
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value, capitalize }) => (
  <div className="flex items-start gap-4 py-3 border-b border-border last:border-0">
    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-foreground font-medium ${capitalize ? "capitalize" : ""}`}>{value}</div>
    </div>
  </div>
);
